import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, Eye, Settings, Plus, Trash2, Move, Type, Palette, AlignLeft, AlignCenter, AlignRight, Image as ImageIcon, Bold, Italic, Underline, Upload } from 'lucide-react';
import { extractSlidesFromContent, exportSlides, cleanupSlideImages, SlideSection } from '../lib/slides';
import { supabase } from '../lib/supabase';

interface SlideEditorProps {
  isOpen: boolean;
  onClose: () => void;
  sermonContent: string;
  sermonTitle: string;
}

interface GlobalStyles {
  backgroundColor: string;
  textColor: string;
  fontFamily: string;
  fontSize: number;
  alignment: 'left' | 'center' | 'right';
  bold: boolean;
  italic: boolean;
  underline: boolean;
  backgroundImage: string;
  padding: number;
  lineHeight: number;
  margin: number;
}

const defaultGlobalStyles: GlobalStyles = {
  backgroundColor: '#FFFFFF',
  textColor: '#1C2B39',
  fontFamily: 'Arial',
  fontSize: 24,
  alignment: 'center',
  bold: false,
  italic: false,
  underline: false,
  backgroundImage: '',
  padding: 20,
  lineHeight: 1.5,
  margin: 20
};

const fontOptions = [
  'Arial',
  'Times New Roman',
  'Calibri',
  'Helvetica',
  'Georgia',
  'Verdana',
  'Tahoma',
  'Impact',
  'Open Sans',
  'Roboto',
  'Lato',
  'Montserrat',
  'Oswald',
  'Raleway',
  'Merriweather',
  'Playfair Display',
  'Ubuntu',
  'Poppins',
  'Nunito',
  'Source Sans Pro',
  'PT Sans',
  'Lora',
  'Inter',
  'Crimson Text',
  'Libre Baskerville',
  'Fira Sans',
  'Work Sans',
  'IBM Plex Sans',
  'Bebas Neue',
  'Comfortaa',
  'Dancing Script',
  'Permanent Marker',
  'Pacifico',
  'Anton',
  'Barlow',
  'DM Sans',
  'Mukti',
  'Quicksand'
];

function SlideEditor({ isOpen, onClose, sermonContent, sermonTitle }: SlideEditorProps) {
  const [slides, setSlides] = useState<SlideSection[]>([]);
  const [selectedSlide, setSelectedSlide] = useState<number>(0);
  const [showPreview, setShowPreview] = useState(false);
  const [showGlobalSettings, setShowGlobalSettings] = useState(false);
  const [globalStyles, setGlobalStyles] = useState<GlobalStyles>(defaultGlobalStyles);
  const [exporting, setExporting] = useState(false);

  // Extract slides when component opens or content changes
  useEffect(() => {
    if (isOpen && sermonContent) {
      const extractedSlides = extractSlidesFromContent(sermonContent);
      if (extractedSlides.length > 0) {
        // Apply default styles to all slides
        const slidesWithStyles = extractedSlides.map(slide => ({
          ...slide,
          style: { ...defaultGlobalStyles }
        }));
        setSlides(slidesWithStyles);
      } else {
        // Create a default slide if none exist
        setSlides([{
          id: 'slide-1',
          type: 'slide',
          content: sermonTitle || 'Welcome',
          title: 'Title Slide',
          style: { ...defaultGlobalStyles }
        }]);
      }
    }
  }, [isOpen, sermonContent, sermonTitle])

  // Apply global styles to all slides when global styles change
  const applyGlobalStyles = useCallback(() => {
    setSlides(currentSlides => 
      currentSlides.map(slide => ({
        ...slide,
        style: { ...globalStyles }
      }))
    );
  }, [globalStyles]);

  const handleExport = async (format: 'pptx' | 'images') => {
    if (slides.length === 0) return;

    setExporting(true);
    try {
      await exportSlides(slides, sermonTitle || 'Sermon Slides', format);

      // Clean up temporary slide images after a successful export
      // Small delay to ensure export is complete
      setTimeout(() => {
        cleanupSlideImages(slides);
      }, 2000);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const addSlide = () => {
    const newSlide: SlideSection = {
      id: `slide-${Date.now()}`,
      type: 'slide',
      content: 'New slide content',
      title: `Slide ${slides.length + 1}`,
      style: { ...globalStyles }
    };
    setSlides([...slides, newSlide]);
    setSelectedSlide(slides.length);
  };

  const deleteSlide = (index: number) => {
    if (slides.length <= 1) return;
    const newSlides = slides.filter((_, i) => i !== index);
    setSlides(newSlides);
    if (selectedSlide >= newSlides.length) {
      setSelectedSlide(newSlides.length - 1);
    }
  };

  const updateSlide = (index: number, updates: Partial<SlideSection>) => {
    const newSlides = [...slides];
    newSlides[index] = { ...newSlides[index], ...updates };
    setSlides(newSlides);
  };

  const moveSlide = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= slides.length) return;
    const newSlides = [...slides];
    const [movedSlide] = newSlides.splice(fromIndex, 1);
    newSlides.splice(toIndex, 0, movedSlide);
    setSlides(newSlides);
    setSelectedSlide(toIndex);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-7xl max-h-[95vh] mx-4 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-sand/20">
          <h2 className="text-xl font-semibold text-midnight">Slide Editor</h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowGlobalSettings(!showGlobalSettings)}
              className="flex items-center px-3 py-2 text-slate hover:text-midnight rounded-lg hover:bg-sand/10 transition-colors"
              type="button"
            >
              <Settings className="h-4 w-4 mr-2" />
              Global Settings
            </button>
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center px-3 py-2 text-slate hover:text-midnight rounded-lg hover:bg-sand/10 transition-colors"
              type="button"
            >
              <Eye className="h-4 w-4 mr-2" />
              {showPreview ? 'Edit' : 'Preview'}
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate hover:text-midnight rounded-full hover:bg-sand/10 transition-colors"
              type="button"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Slide List */}
          <div className="w-80 border-r border-sand/20 flex flex-col">
            <div className="p-4 border-b border-sand/20">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-midnight">Slides ({slides.length})</h3>
                <button
                  onClick={addSlide}
                  className="flex items-center px-3 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 transition-colors text-sm"
                  type="button"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </button>
              </div>

              {/* Export Buttons */}
              <div className="space-y-2">
                <button
                  onClick={() => handleExport('pptx')}
                  disabled={exporting}
                  className="w-full flex items-center justify-center px-3 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90 transition-colors text-sm disabled:opacity-50"
                  type="button"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {exporting ? 'Exporting...' : 'Export PowerPoint'}
                </button>
                <button
                  onClick={() => handleExport('images')}
                  disabled={exporting}
                  className="w-full flex items-center justify-center px-3 py-2 bg-gold text-white rounded-lg hover:bg-gold/90 transition-colors text-sm disabled:opacity-50"
                  type="button"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {exporting ? 'Exporting...' : 'Export Images'}
                </button>
              </div>
            </div>

            {/* Slide Thumbnails */}
            <div className="flex-1 overflow-y-auto p-2">
              {slides.map((slide, index) => (
                <SlideThumbnail
                  key={slide.id}
                  slide={slide}
                  index={index}
                  isSelected={selectedSlide === index}
                  onClick={() => setSelectedSlide(index)}
                  onDelete={() => deleteSlide(index)}
                  onMoveUp={() => moveSlide(index, index - 1)}
                  onMoveDown={() => moveSlide(index, index + 1)}
                  canMoveUp={index > 0}
                  canMoveDown={index < slides.length - 1}
                  canDelete={slides.length > 1}
                />
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            {showGlobalSettings && (
              <GlobalSettingsPanel
                globalStyles={globalStyles}
                onStyleChange={setGlobalStyles}
                onApplyToAll={applyGlobalStyles}
              />
            )}

            {showPreview ? (
              <SlidePreview slide={slides[selectedSlide]} />
            ) : (
              <SlideEditPanel
                slide={slides[selectedSlide]}
                onSlideChange={(updates) => updateSlide(selectedSlide, updates)}
                globalStyles={globalStyles}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Global Settings Panel Component
interface GlobalSettingsPanelProps {
  globalStyles: GlobalStyles;
  onStyleChange: (styles: GlobalStyles) => void;
  onApplyToAll: () => void;
}

function GlobalSettingsPanel({ globalStyles, onStyleChange, onApplyToAll }: GlobalSettingsPanelProps) {
  const updateStyle = (key: keyof GlobalStyles, value: any) => {
    onStyleChange({ ...globalStyles, [key]: value });
  };

  return (
    <div className="p-4 border-b border-sand/20 bg-sand/5 max-h-96 overflow-y-auto flex-shrink-0">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-midnight">Global Slide Settings</h3>
        <button
          onClick={onApplyToAll}
          className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 transition-colors text-sm"
          type="button"
        >
          Apply to All Slides
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Background Settings */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-slate">Background</h4>
          <div>
            <label className="block text-xs text-slate mb-1">Background Color</label>
            <input
              type="color"
              value={globalStyles.backgroundColor}
              onChange={(e) => updateStyle('backgroundColor', e.target.value)}
              className="w-full h-8 rounded border border-sand/20"
            />
          </div>
          <div>
            <label className="block text-xs text-slate mb-1">Background Image</label>
            <ImageUploadComponent
              currentImage={globalStyles.backgroundImage}
              onImageChange={(imageUrl) => updateStyle('backgroundImage', imageUrl)}
            />
          </div>
        </div>

        {/* Text Settings */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-slate">Text</h4>
          <div>
            <label className="block text-xs text-slate mb-1">Text Color</label>
            <input
              type="color"
              value={globalStyles.textColor}
              onChange={(e) => updateStyle('textColor', e.target.value)}
              className="w-full h-8 rounded border border-sand/20"
            />
          </div>
          <div>
            <label className="block text-xs text-slate mb-1">Font Family</label>
            <select
              value={globalStyles.fontFamily}
              onChange={(e) => updateStyle('fontFamily', e.target.value)}
              className="w-full px-3 py-2 border border-sand/20 rounded-lg text-sm"
            >
              {fontOptions.map(font => (
                <option key={font} value={font}>{font}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate mb-1">Font Size: {globalStyles.fontSize}px</label>
            <input
              type="range"
              min="12"
              max="72"
              value={globalStyles.fontSize}
              onChange={(e) => updateStyle('fontSize', parseInt(e.target.value))}
              className="w-full"
            />
          </div>
        </div>

        {/* Layout Settings */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-slate">Layout</h4>
          <div>
            <label className="block text-xs text-slate mb-1">Text Alignment</label>
            <div className="flex space-x-1">
              {[
                { value: 'left', icon: AlignLeft },
                { value: 'center', icon: AlignCenter },
                { value: 'right', icon: AlignRight }
              ].map(({ value, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => updateStyle('alignment', value)}
                  className={`p-2 rounded border ${
                    globalStyles.alignment === value
                      ? 'bg-electric text-white border-electric'
                      : 'bg-white text-slate border-sand/20 hover:bg-sand/10'
                  }`}
                  type="button"
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate mb-1">Line Height: {globalStyles.lineHeight}</label>
            <input
              type="range"
              min="1"
              max="3"
              step="0.1"
              value={globalStyles.lineHeight}
              onChange={(e) => updateStyle('lineHeight', parseFloat(e.target.value))}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-xs text-slate mb-1">Padding: {globalStyles.padding}px</label>
            <input
              type="range"
              min="0"
              max="100"
              value={globalStyles.padding}
              onChange={(e) => updateStyle('padding', parseInt(e.target.value))}
              className="w-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Slide Thumbnail Component
interface SlideThumbnailProps {
  slide: SlideSection;
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  canDelete: boolean;
}

function SlideThumbnail({
  slide,
  index,
  isSelected,
  onClick,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  canDelete
}: SlideThumbnailProps) {
  return (
    <div
      className={`relative p-3 mb-2 border rounded-lg cursor-pointer transition-colors ${
        isSelected
          ? 'border-electric bg-electric/10'
          : 'border-sand/20 hover:border-sand/40'
      }`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-midnight">
          {slide.title || `Slide ${index + 1}`}
        </span>
        <div className="flex space-x-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMoveUp();
            }}
            disabled={!canMoveUp}
            className="p-1 text-slate hover:text-midnight disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
          >
            <Move className="h-3 w-3 rotate-180" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMoveDown();
            }}
            disabled={!canMoveDown}
            className="p-1 text-slate hover:text-midnight disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
          >
            <Move className="h-3 w-3" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            disabled={!canDelete}
            className="p-1 text-crimson hover:text-crimson/80 disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>

      <div
        className="w-full h-16 rounded text-xs overflow-hidden flex items-center justify-center text-center p-2"
        style={{
          backgroundColor: slide.style?.backgroundColor || '#FFFFFF',
          color: slide.style?.textColor || '#1C2B39',
          fontFamily: slide.style?.fontFamily || 'Arial',
          backgroundImage: slide.style?.backgroundImage ? `url(${slide.style.backgroundImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {slide.content.substring(0, 50)}...
      </div>
    </div>
  );
}

// Slide Preview Component
interface SlidePreviewProps {
  slide: SlideSection;
}

function SlidePreview({ slide }: SlidePreviewProps) {
  if (!slide) return null;

  return (
    <div className="flex-1 p-6 flex items-center justify-center bg-slate/5 overflow-y-auto max-h-full">
      <div className="w-full max-w-4xl">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-midnight mb-2">Preview - 16:9 Aspect Ratio</h3>
          <p className="text-sm text-slate">This preview shows how your slide will appear in presentation mode.</p>
        </div>

        <div
          className="w-full aspect-video rounded-lg shadow-lg flex flex-col items-center justify-center p-8 text-center"
          style={{
            backgroundColor: slide.style?.backgroundColor || '#FFFFFF',
            color: slide.style?.textColor || '#1C2B39',
            fontFamily: slide.style?.fontFamily || 'Arial',
            backgroundImage: slide.style?.backgroundImage ? `url(${slide.style.backgroundImage})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {slide.title && (
            <h1
              className="mb-4"
              style={{
                fontSize: `${(slide.style?.fontSize || 24) * 1.2}px`,
                fontWeight: slide.style?.bold ? 'bold' : 'bold',
                fontStyle: slide.style?.italic ? 'italic' : 'normal',
                textDecoration: slide.style?.underline ? 'underline' : 'none',
                textAlign: slide.style?.alignment || 'center',
                lineHeight: slide.style?.lineHeight || 1.5
              }}
            >
              {slide.title}
            </h1>
          )}

          <div
            style={{
              fontSize: `${slide.style?.fontSize || 24}px`,
              fontWeight: slide.style?.bold ? 'bold' : 'normal',
              fontStyle: slide.style?.italic ? 'italic' : 'normal',
              textDecoration: slide.style?.underline ? 'underline' : 'none',
              textAlign: slide.style?.alignment || 'center',
              lineHeight: slide.style?.lineHeight || 1.5,
              maxWidth: '90%'
            }}
          >
            {slide.content}
          </div>
        </div>
      </div>
    </div>
  );
}

// Slide Edit Panel Component
interface SlideEditPanelProps {
  slide: SlideSection;
  onSlideChange: (updates: Partial<SlideSection>) => void;
  globalStyles: GlobalStyles;
}

function SlideEditPanel({ slide, onSlideChange, globalStyles }: SlideEditPanelProps) {
  // Remove the problematic useEffect that was causing infinite loops

  const updateSlideStyle = (key: keyof GlobalStyles, value: any) => {
    const newStyle = { ...slide.style, [key]: value };
    onSlideChange({ style: newStyle });
  };

  if (!slide) {
    return (
      <div className="flex-1 flex items-center justify-center text-slate">
        <p>Select a slide to edit</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex">
      {/* Edit Controls */}
      <div className="w-80 border-r border-sand/20 p-4 overflow-y-auto max-h-full">
        <h3 className="font-medium text-midnight mb-4">Edit Slide</h3>

        {/* Title */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate mb-2">Title</label>
          <input
            type="text"
            value={slide.title || ''}
            onChange={(e) => onSlideChange({ title: e.target.value })}
            className="w-full px-3 py-2 border border-sand/20 rounded-lg"
            placeholder="Slide title"
          />
        </div>

        {/* Content */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate mb-2">Content</label>
          <textarea
            value={slide.content}
            onChange={(e) => onSlideChange({ content: e.target.value })}
            className="w-full px-3 py-2 border border-sand/20 rounded-lg h-32 resize-none"
            placeholder="Slide content"
          />
        </div>

        {/* Individual Slide Styling */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-slate">Individual Slide Styling</h4>

          {/* Background */}
          <div>
            <label className="block text-xs text-slate mb-1">Background Color</label>
            <input
              type="color"
              value={slide.style?.backgroundColor || globalStyles.backgroundColor}
              onChange={(e) => updateSlideStyle('backgroundColor', e.target.value)}
              className="w-full h-8 rounded border border-sand/20"
            />
          </div>

          {/* Text Color */}
          <div>
            <label className="block text-xs text-slate mb-1">Text Color</label>
            <input
              type="color"
              value={slide.style?.textColor || globalStyles.textColor}
              onChange={(e) => updateSlideStyle('textColor', e.target.value)}
              className="w-full h-8 rounded border border-sand/20"
            />
          </div>

          {/* Font Family */}
          <div>
            <label className="block text-xs text-slate mb-1">Font Family</label>
            <select
              value={slide.style?.fontFamily || globalStyles.fontFamily}
              onChange={(e) => updateSlideStyle('fontFamily', e.target.value)}
              className="w-full px-3 py-2 border border-sand/20 rounded-lg text-sm"
            >
              {fontOptions.map(font => (
                <option key={font} value={font}>{font}</option>
              ))}
            </select>
          </div>

          {/* Font Size */}
          <div>
            <label className="block text-xs text-slate mb-1">
              Font Size: {slide.style?.fontSize || globalStyles.fontSize}px
            </label>
            <input
              type="range"
              min="12"
              max="72"
              value={slide.style?.fontSize || globalStyles.fontSize}
              onChange={(e) => updateSlideStyle('fontSize', parseInt(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Text Alignment */}
          <div>
            <label className="block text-xs text-slate mb-1">Text Alignment</label>
            <div className="flex space-x-1">
              {[
                { value: 'left', icon: AlignLeft },
                { value: 'center', icon: AlignCenter },
                { value: 'right', icon: AlignRight }
              ].map(({ value, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => updateSlideStyle('alignment', value)}
                  className={`p-2 rounded border ${
                    (slide.style?.alignment || globalStyles.alignment) === value
                      ? 'bg-electric text-white border-electric'
                      : 'bg-white text-slate border-sand/20 hover:bg-sand/10'
                  }`}
                  type="button"
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
          </div>

          {/* Text Style */}
          <div>
            <label className="block text-xs text-slate mb-1">Text Style</label>
            <div className="flex space-x-1">
              <button
                onClick={() => updateSlideStyle('bold', !(slide.style?.bold || globalStyles.bold))}
                className={`p-2 rounded border ${
                  slide.style?.bold || globalStyles.bold
                    ? 'bg-electric text-white border-electric'
                    : 'bg-white text-slate border-sand/20 hover:bg-sand/10'
                }`}
                type="button"
              >
                <Bold className="h-4 w-4" />
              </button>
              <button
                onClick={() => updateSlideStyle('italic', !(slide.style?.italic || globalStyles.italic))}
                className={`p-2 rounded border ${
                  slide.style?.italic || globalStyles.italic
                    ? 'bg-electric text-white border-electric'
                    : 'bg-white text-slate border-sand/20 hover:bg-sand/10'
                }`}
                type="button"
              >
                <Italic className="h-4 w-4" />
              </button>
              <button
                onClick={() => updateSlideStyle('underline', !(slide.style?.underline || globalStyles.underline))}
                className={`p-2 rounded border ${
                  slide.style?.underline || globalStyles.underline
                    ? 'bg-electric text-white border-electric'
                    : 'bg-white text-slate border-sand/20 hover:bg-sand/10'
                }`}
                type="button"
              >
                <Underline className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Live Preview */}
      <div className="flex-1 p-6 bg-slate/5 overflow-y-auto max-h-full">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-midnight mb-2">Live Preview</h3>
          <p className="text-sm text-slate">Changes are applied in real-time.</p>
        </div>

        <div
          className="w-full aspect-video rounded-lg shadow-lg flex flex-col items-center justify-center p-8 text-center max-w-4xl mx-auto"
          style={{
            backgroundColor: slide.style?.backgroundColor || globalStyles.backgroundColor,
            color: slide.style?.textColor || globalStyles.textColor,
            fontFamily: slide.style?.fontFamily || globalStyles.fontFamily,
            backgroundImage: slide.style?.backgroundImage || globalStyles.backgroundImage ? 
              `url(${slide.style?.backgroundImage || globalStyles.backgroundImage})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {slide.title && (
            <h1
              className="mb-4"
              style={{
                fontSize: `${(slide.style?.fontSize || globalStyles.fontSize) * 1.2}px`,
                fontWeight: (slide.style?.bold ?? globalStyles.bold) ? 'bold' : 'bold',
                fontStyle: (slide.style?.italic ?? globalStyles.italic) ? 'italic' : 'normal',
                textDecoration: (slide.style?.underline ?? globalStyles.underline) ? 'underline' : 'none',
                textAlign: slide.style?.alignment || globalStyles.alignment,
                lineHeight: slide.style?.lineHeight || globalStyles.lineHeight
              }}
            >
              {slide.title}
            </h1>
          )}

          <div
            style={{
              fontSize: `${slide.style?.fontSize || globalStyles.fontSize}px`,
              fontWeight: (slide.style?.bold ?? globalStyles.bold) ? 'bold' : 'normal',
              fontStyle: (slide.style?.italic ?? globalStyles.italic) ? 'italic' : 'normal',
              textDecoration: (slide.style?.underline ?? globalStyles.underline) ? 'underline' : 'none',
              textAlign: slide.style?.alignment || globalStyles.alignment,
              lineHeight: slide.style?.lineHeight || globalStyles.lineHeight,
              maxWidth: '90%'
            }}
          >
            {slide.content}
          </div>
        </div>
      </div>
    </div>
  );
}

// Image Upload Component
interface ImageUploadComponentProps {
  currentImage: string;
  onImageChange: (imageUrl: string) => void;
}

function ImageUploadComponent({ currentImage, onImageChange }: ImageUploadComponentProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create a unique file path for slide images
      const fileExt = file.name.split('.').pop();
      const fileName = `slide-bg-${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/slide-images/${fileName}`;

      // Upload to Supabase storage
      const { data, error } = await supabase.storage
        .from('sermon-media')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('sermon-media')
        .getPublicUrl(filePath);

      console.log('Image uploaded successfully:', publicUrl);
      onImageChange(publicUrl);
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    onImageChange('');
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <label className="flex items-center px-3 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 transition-colors cursor-pointer text-sm">
          <Upload className="h-4 w-4 mr-2" />
          {uploading ? 'Uploading...' : 'Upload Image'}
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            disabled={uploading}
            className="hidden"
          />
        </label>
        {currentImage && (
          <button
            onClick={removeImage}
            className="px-3 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90 transition-colors text-sm"
            type="button"
          >
            Remove
          </button>
        )}
      </div>

      {uploadError && (
        <p className="text-xs text-crimson">{uploadError}</p>
      )}

      {currentImage && (
        <div className="border border-sand/20 rounded-lg overflow-hidden">
          <img
            src={currentImage}
            alt="Background preview"
            className="w-full h-20 object-cover"
            onError={(e) => {
              console.error('Failed to load preview image:', currentImage);
              setUploadError('Image preview failed to load');
            }}
            onLoad={() => {
              console.log('Image preview loaded successfully');
              setUploadError(null);
            }}
          />
          <p className="text-xs text-slate p-2 bg-sand/5">Current background image</p>
        </div>
      )}
    </div>
  );
}

export default SlideEditor;